import React, { useEffect, useRef, useState } from 'react'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { AdCreative, PacketStrategyGroup } from '@marketing-milk/interfaces'
import { CreativeStatus } from 'components/creative-status/CreativeStatus'
import { CreativeMedia } from '../../../creatives/CreativeCard/CreativeMedia'
import {
  isAdImage,
  isAdVideo,
  isDynamicCreative,
} from '../../../campaigns/helpers/campaign.helpers'
import { accent, accentSoft, radius, SurfacePalette, type } from '../../tokens'
import { CommentIndicator } from '../../components/CommentIndicator'
import { useUnresolvedCountLookup } from '../UnresolvedCommentsContext'

/**
 * Packet-local thumbnail. For dynamic creatives whose lead asset is a video we
 * render the `thumbnail` poster (never-blank, consistent with the dynamic asset
 * pool) instead of the shared `CreativeMedia`'s autoplaying `<video src=source>` —
 * patching `CreativeMedia` itself would change every other creative screen. All
 * non-dynamic-video cases delegate to the shared `CreativeMedia`.
 */
const CardThumbnail = ({ creative }: { creative: AdCreative }) => {
  if (isDynamicCreative(creative)) {
    const lead = [...creative.dynamicImages, ...creative.dynamicVideos][0]
    if (isAdVideo(lead) && lead.thumbnail) {
      return (
        <img
          src={lead.thumbnail}
          alt="creative video thumbnail"
          className="h-full w-full object-cover rounded-t-lg"
        />
      )
    }
    if (isAdImage(lead)) {
      return (
        <img src={lead.url} alt={lead.name} className="h-full w-full object-cover rounded-t-lg" />
      )
    }
  }
  return <CreativeMedia creative={creative} className="h-full w-full" />
}

/** Shared shape of the media-tile badges; each usage sets its own background. */
const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 8px',
  borderRadius: radius.sm,
  color: '#fff',
  fontFamily: type.family.sans,
  fontSize: type.size.eyebrow,
  fontWeight: type.weight.semibold,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}

export interface CreativeCardEditing {
  /** Groups this ad can be moved into. Ungrouped is deliberately not one. */
  groups: PacketStrategyGroup[]
  onMove: (creativeID: number, groupID: number) => void
  onUnpin: (creativeID: number) => void
  onSaveJustification: (creativeID: number, justification: string | null) => Promise<void>
}

interface Props {
  palette: SurfacePalette
  creative: AdCreative
  /** True when the AE placed this ad by hand, so regeneration will leave it alone. */
  pinned?: boolean
  onPreview?: (creative: AdCreative) => void
  onPrefetch?: (creative: AdCreative) => void
  /** Present when the card is in Edit mode. Present mode passes nothing. */
  editing?: CreativeCardEditing
}

/**
 * One creative tile inside the packet's Creative section, shared by Edit and
 * Present. Beyond the thumbnail it carries the strategist's justification
 * (client-visible in both modes, editable in Edit) and — in Edit only — the
 * pin marker plus the move menu.
 */
export const CreativeCard = ({
  palette,
  creative,
  pinned,
  onPreview,
  onPrefetch,
  editing,
}: Props) => {
  const countFor = useUnresolvedCountLookup()
  // Dynamic creatives open the grouped asset pool (not the FB feed preview),
  // so flag them up-front: accent border + a "Dynamic" badge, and a
  // "View assets" hover label instead of "Preview ad".
  const dynamic = isDynamicCreative(creative)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  return (
    <div
      className="group"
      data-comment-anchor-type="creative"
      data-comment-anchor-id={String(creative.id)}
      data-comment-label={creative.name}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: radius.md,
        border: `1px solid ${dynamic ? accent : palette.hairline}`,
        boxShadow: dynamic ? `0 0 0 1px ${accentSoft}` : undefined,
        background: palette.surface,
        overflow: 'hidden',
      }}
    >
      {/*
       * The card is a gallery tile: media is the click target that opens the
       * preview (where the full ad + comment / approve / open-source actions
       * live). The status chip and comment overlay below are SIBLINGS of this
       * element, not children, so clicking the comment trigger never fires
       * the preview handler and still bubbles to the `useCommentAnchors` root.
       */}
      <div
        role={onPreview ? 'button' : undefined}
        tabIndex={onPreview ? 0 : undefined}
        aria-label={onPreview ? `Preview ${creative.name}` : undefined}
        onClick={onPreview ? () => onPreview(creative) : undefined}
        onMouseEnter={onPrefetch && !dynamic ? () => onPrefetch(creative) : undefined}
        onKeyDown={
          onPreview
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onPreview(creative)
                }
              }
            : undefined
        }
        style={{
          position: 'relative',
          aspectRatio: '1 / 1',
          // A flex item's automatic minimum size lets a portrait <video>'s
          // intrinsic height override the aspect-ratio square; zero it so
          // videos crop to the tile like images do.
          minHeight: 0,
          background: palette.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: onPreview ? 'pointer' : 'default',
        }}
      >
        {/*
         * Reuse the app-wide CreativeMedia thumbnail (image/video/carousel/
         * dynamic with broken-link fallbacks) rather than the single
         * denormalized s3Preview field, which is empty for most creatives.
         */}
        <CardThumbnail creative={creative} />
        {onPreview && (
          // Faint hover overlay so the media reads as an interactive preview
          // target. group-hover is driven by the card wrapper's `group` class.
          <div
            className="opacity-0 group-hover:opacity-100"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.4)',
              color: '#fff',
              fontFamily: type.family.sans,
              fontSize: type.size.eyebrow,
              fontWeight: type.weight.medium,
              transition: 'opacity 120ms ease',
              pointerEvents: 'none',
            }}
          >
            {dynamic ? 'View assets' : 'Preview ad'}
          </div>
        )}

        {/* Badges — anchored to the bottom-left of the MEDIA tile (this
          relatively-positioned wrapper), not the card, so they sit on the
          thumbnail rather than over the caption below. */}
        {(dynamic || creative.leadCardUsed || pinned) && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              display: 'flex',
              gap: 6,
            }}
          >
            {dynamic && (
              <div style={{ ...badgeStyle, background: accent, pointerEvents: 'none' }}>
                <i className="fa fa-random" style={{ fontSize: 10 }} />
                Dynamic
              </div>
            )}
            {/* Lead-form chip: this creative's preview embeds the lead card
              journey, so the AE can spot lead ads before opening anything. */}
            {creative.leadCardUsed && (
              <div
                style={{ ...badgeStyle, background: 'rgba(0, 0, 0, 0.55)', pointerEvents: 'none' }}
              >
                <i className="fa fa-address-card" style={{ fontSize: 10 }} />
                Lead form
              </div>
            )}
            {/* Pins have to be visible AND undoable — a pin the AE can't clear
              is a trap, since regeneration would never move the ad again. */}
            {pinned &&
              (editing ? (
                <button
                  type="button"
                  aria-label={`Unpin ${creative.name}`}
                  title="Pinned here by hand — click to unpin"
                  onClick={e => {
                    e.stopPropagation()
                    editing.onUnpin(creative.id)
                  }}
                  style={{
                    ...badgeStyle,
                    background: 'rgba(0, 0, 0, 0.7)',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <i className="fa fa-thumb-tack" style={{ fontSize: 10 }} />
                  Pinned
                </button>
              ) : (
                <div
                  style={{
                    ...badgeStyle,
                    background: 'rgba(0, 0, 0, 0.55)',
                    pointerEvents: 'none',
                  }}
                >
                  <i className="fa fa-thumb-tack" style={{ fontSize: 10 }} />
                  Pinned
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Status chip overlay (top-left), keeping its own CreativeStatus badge. */}
      <div style={{ position: 'absolute', top: 10, left: 10, pointerEvents: 'none' }}>
        <CreativeStatus creative={creative} className="shadow-sm" />
      </div>

      {/* Comment overlay (top-right), sibling of the media region. */}
      <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6 }}>
        {/* Moving is Edit-only: re-sorting in front of the client would
          reshuffle the deck mid-meeting. */}
        {editing && editing.groups.length > 0 && (
          <>
            <button
              type="button"
              aria-label={`Move ${creative.name} to another group`}
              onClick={e => setMenuAnchor(e.currentTarget)}
              style={{
                background: 'rgba(0, 0, 0, 0.55)',
                color: '#fff',
                border: 'none',
                borderRadius: radius.sm,
                cursor: 'pointer',
                padding: '4px 8px',
                fontSize: 12,
                lineHeight: 1,
              }}
            >
              <i className="fa fa-exchange" />
            </button>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
              MenuListProps={{ dense: true }}
            >
              {editing.groups.map(group => (
                <MenuItem
                  key={group.id}
                  onClick={() => {
                    setMenuAnchor(null)
                    editing.onMove(creative.id, group.id)
                  }}
                >
                  {group.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        <CommentIndicator
          palette={palette}
          label={creative.name}
          iconOnly
          unresolvedCount={countFor('creative', String(creative.id))}
          style={{
            background: 'rgba(0, 0, 0, 0.55)',
            color: '#fff',
            borderRadius: radius.sm,
          }}
        />
      </div>

      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span
          style={{
            fontFamily: type.family.sans,
            fontSize: type.size.body,
            fontWeight: type.weight.medium,
            color: palette.text,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.35,
          }}
          title={creative.name}
        >
          {creative.name}
        </span>
        <Justification
          palette={palette}
          creative={creative}
          onSave={editing ? editing.onSaveJustification : undefined}
        />
      </div>
    </div>
  )
}

/**
 * The strategist's reasoning for this ad. Client-visible in both modes — there
 * is deliberately no visibility toggle and no speaker-notes mode; if it isn't
 * fit for the client to read, it should be reworded, which is why Edit can.
 *
 * Save-on-blur, matching the narrative editors.
 */
const Justification = ({
  palette,
  creative,
  onSave,
}: {
  palette: SurfacePalette
  creative: AdCreative
  onSave?: (creativeID: number, justification: string | null) => Promise<void>
}) => {
  const stored = creative.justification ?? ''
  const [text, setText] = useState(stored)
  const lastSavedRef = useRef(stored)

  useEffect(() => {
    if (stored !== lastSavedRef.current && text === lastSavedRef.current) {
      setText(stored)
    }
    lastSavedRef.current = stored
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored])

  const style: React.CSSProperties = {
    margin: 0,
    fontFamily: type.family.sans,
    fontSize: type.size.eyebrow,
    color: palette.textMuted,
    lineHeight: 1.45,
  }

  if (!onSave) {
    return stored ? <p style={style}>{stored}</p> : null
  }

  return (
    <textarea
      value={text}
      aria-label={`Why we made ${creative.name}`}
      placeholder="Why we made this…"
      maxLength={2000}
      onChange={e => setText(e.target.value)}
      onBlur={async () => {
        const next = text.trim()
        if (next === lastSavedRef.current) return
        try {
          await onSave(creative.id, next || null)
          lastSavedRef.current = next
          setText(next)
        } catch {
          setText(lastSavedRef.current)
        }
      }}
      style={{
        ...style,
        width: '100%',
        minHeight: 48,
        resize: 'vertical',
        background: 'transparent',
        border: `1px solid ${palette.hairline}`,
        borderRadius: radius.sm,
        padding: 8,
      }}
    />
  )
}
